import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../supabase';
import { mfaTotpIssuer } from '../../../utils/auth/mfaTotpIssuer';
import { issueRecoveryCodes as issueRecoveryCodesRequest } from '../../../services/auth/mfaRecoveryService';
import { withTimeout } from '../../../utils/async/withTimeout';
import { ACTION_BEAT_TIMEOUT_MS } from '../../../hooks/ui/useModalActionBeat';
import { startMfaExitLogoHandoff } from '../../../ui/splash/startMfaExitLogoHandoff';
import { prefersReducedMotion } from '../../../ui/splash/splashGlide';

/** Duration of the green "verified" OTP-cell beat before the flow advances —
 *  matches the legacy pre-stepper enroll flow / MfaVerifyScreen feel. A plain
 *  timer (never an animationend callback) so it always resolves. */
export const VERIFIED_BEAT_MS = 450;
/** Wrong-code cell shake/red window before the cells reset to idle. */
const ERROR_RESET_MS = 400;

/**
 * RD-SEC-015 — the MFA enrollment stepper's state machine: step, in-memory
 * enroll payload, code entry + miss count, resume detection, and issuing
 * recovery codes. Shared by the Account modal (`mode: 'account'`) and both
 * forced gates (`mode: 'gate'`) — it owns the `onRecoveryHoldChange` /
 * `onEnrollmentComplete` calls exactly per the LOCKED gate contract (see
 * `TenantMfaEnrollmentGate` / `ProtectedRoute.mfaGate.test.js`): the hold
 * arms the instant verify succeeds and is held through codes + done; the gate
 * clears ONLY via `doneAction()` (wired to "Ir al inicio"); a code-issue
 * failure lands on Done with a clear warning, so the user can choose to
 * continue without implying recovery codes exist.
 *
 * Steps: 'checking' (mount-time resume probe) → 'intro' → 'add' → 'code' →
 * 'codes' → 'done', OR (resume flow) 'checking' → 'resume' → 'codes' → 'done'.
 *
 * No client-side audit write and no `logAuthSecurityEvent` call for enroll —
 * the DB lifecycle sweeper (Lane A) is the sole writer now.
 */
export default function useMfaEnrollment({ mode = 'account', onEnrollmentComplete, onRecoveryHoldChange } = {}) {
  const [step, setStep] = useState('checking');
  const [payload, setPayload] = useState(null); // { id, qr_code, secret, uri }
  const [code, setCode] = useState('');
  const [codeStatus, setCodeStatus] = useState('idle'); // 'idle' | 'error' | 'verified'
  const [missCount, setMissCount] = useState(0);
  const [codes, setCodes] = useState(null); // string[] | null
  // Recovery-code issuance failed → Done explains why it shows 0.
  const [codesFailed, setCodesFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [startError, setStartError] = useState('');
  const [fromApp, setFromApp] = useState(false); // iPhone handoff return (flow G) — code subtitle
  const [resumeFlow, setResumeFlow] = useState(false); // sticky once the resume path is entered

  const unverifiedFactorRef = useRef(null);
  const verifyingRef = useRef(false);
  const errorTimerRef = useRef(null);

  const clearErrorTimer = useCallback(() => {
    if (errorTimerRef.current != null) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => clearErrorTimer(), [clearErrorTimer]);

  // Reset the code entry only once the OTP steps are gone (FadeStep has no
  // leaving snapshot, so the code step is already unmounted by this commit).
  useEffect(() => {
    if (step !== 'codes' && step !== 'done') return;
    setCode('');
    setCodeStatus('idle');
    setMissCount(0);
  }, [step]);

  // Mount-time resume probe (flow E): an unverified TOTP with no in-memory
  // payload means a prior enrollment attempt never finished (e.g. an iOS
  // reload mid-handoff). Only resolves the 'checking' step — a user who
  // already tapped Empezar before this resolves is left alone.
  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (!active) return;
      if (error) {
        setStep((cur) => (cur === 'checking' ? 'intro' : cur));
        return;
      }
      const unverified = (data?.totp ?? []).find((f) => f.status === 'unverified') ?? null;
      unverifiedFactorRef.current = unverified;
      setStep((cur) => {
        if (cur !== 'checking') return cur;
        if (unverified) {
          setResumeFlow(true);
          return 'resume';
        }
        return 'intro';
      });
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFactorId = payload?.id ?? unverifiedFactorRef.current?.id ?? null;

  /** Empezar (intro→add). Reuses the in-memory payload if one already exists
   *  (‹ back to intro must never re-enroll — flow A). */
  const startEnroll = useCallback(async () => {
    if (payload) {
      setStep('add');
      return;
    }
    setBusy(true);
    setStartError('');
    try {
      const { data, error } = await withTimeout(supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: mfaTotpIssuer(),
        friendlyName: `crewvex-${Date.now()}`,
      }), ACTION_BEAT_TIMEOUT_MS);
      if (error) throw error;
      setPayload({ id: data.id, qr_code: data.totp.qr_code, secret: data.totp.secret, uri: data.totp.uri });
      setCode('');
      setCodeStatus('idle');
      setMissCount(0);
      setStep('add');
    } catch (err) {
      // Raw error text never reaches the UI (KAN-590): MfaStepIntro renders its own
      // localized line whenever this is truthy; the cause is logged.
      console.warn('[mfa] enroll start failed', err); // eslint-disable-line no-console
      setStartError('start-failed');
    } finally {
      setBusy(false);
    }
  }, [payload]);

  const goToCode = useCallback(() => {
    setCode('');
    setCodeStatus('idle');
    setStep('code');
  }, []);

  /** ‹ from code → add: keep the payload, reset the code entry + miss count. */
  const goBackToAdd = useCallback(() => {
    setCode('');
    setCodeStatus('idle');
    setMissCount(0);
    setFromApp(false);
    setStep('add');
  }, []);

  /** ‹ from add → intro: keep the in-memory payload (no unenroll). */
  const goBackToIntro = useCallback(() => {
    setStep('intro');
  }, []);

  const issueCodes = useCallback(async () => {
    onRecoveryHoldChange?.(true);
    try {
      const result = await withTimeout(issueRecoveryCodesRequest(), ACTION_BEAT_TIMEOUT_MS);
      if (result?.error) throw new Error(result.error);
      const issued = Array.isArray(result?.codes) ? result.codes : [];
      setCodesFailed(false);
      setCodes(issued);
      setStep('codes');
    } catch (_err) {
      // Keep the gate until the user acknowledges Done's warning. A timed-out
      // request is not evidence that codes exist.
      setCodes([]);
      setCodesFailed(true);
      setStep('done');
    }
  }, [onRecoveryHoldChange]);

  const verify = useCallback(async (rawCode) => {
    const digits = String(rawCode ?? '').replace(/\D/g, '');
    const factorId = activeFactorId;
    if (digits.length !== 6 || !factorId || verifyingRef.current) return;
    verifyingRef.current = true;
    setBusy(true);
    clearErrorTimer();
    try {
      const { error } = await withTimeout(
        supabase.auth.mfa.challengeAndVerify({ factorId, code: digits }),
        ACTION_BEAT_TIMEOUT_MS,
      );
      if (error) throw error;
      setCodeStatus('verified');
      // Arm the hold SYNCHRONOUSLY on success — the user is AAL2 the instant
      // verify resolves, so the gate must hold through the beat + code issuance.
      onRecoveryHoldChange?.(true);
      await new Promise((resolve) => setTimeout(resolve, VERIFIED_BEAT_MS));
      unverifiedFactorRef.current = null;
      setPayload(null);
      // The green, FILLED digits hold until the step actually changes (codes
      // issuance is a network call). The code/status reset happens in the
      // step effect below, after the code step has unmounted — clearing here
      // flashed six blank cells between "Verified" and step 3 (2026-09-25).
      await issueCodes();
    } catch (_err) {
      setMissCount((c) => c + 1);
      setCodeStatus('error');
      setCode('');
      errorTimerRef.current = setTimeout(() => {
        errorTimerRef.current = null;
        setCodeStatus('idle');
      }, ERROR_RESET_MS);
    } finally {
      verifyingRef.current = false;
      setBusy(false);
    }
  }, [activeFactorId, clearErrorTimer, issueCodes, onRecoveryHoldChange]);

  /** Resume's "Empezar de nuevo" — unenroll the stranded factor, back to a
   *  fresh intro (flow E). */
  const startOver = useCallback(async () => {
    const factorId = unverifiedFactorRef.current?.id;
    unverifiedFactorRef.current = null;
    setBusy(true);
    try {
      if (factorId) await withTimeout(supabase.auth.mfa.unenroll({ factorId }), ACTION_BEAT_TIMEOUT_MS);
    } catch (_e) {
      /* best-effort — a stranded unverified factor is inert; a timeout here
         is the same "give up gracefully" outcome as any other failure. */
    } finally {
      setBusy(false);
    }
    setPayload(null);
    setCode('');
    setCodeStatus('idle');
    setMissCount(0);
    setFromApp(false);
    setResumeFlow(false);
    setStep('intro');
  }, []);

  /** ✕ on add/code — unenroll the pending unverified factor silently, no
   *  toast (flow A). Resolves before the caller closes the modal. */
  const cancelPending = useCallback(async () => {
    const factorId = payload?.id;
    setPayload(null);
    setCode('');
    setCodeStatus('idle');
    setMissCount(0);
    setFromApp(false);
    if (factorId) {
      try {
        await withTimeout(supabase.auth.mfa.unenroll({ factorId }), ACTION_BEAT_TIMEOUT_MS);
      } catch (_e) {
        /* best-effort, no toast — see flow A; a timeout is the same
           "give up gracefully" outcome as any other failure here. */
      }
    }
  }, [payload]);

  /** Done step's primary button — "Listo" (Account) / "Ir al inicio" (gate).
   *  The gate clears ONLY through this call (LOCKED hold/complete contract).
   *  Gate mode (RD-SEC-021d): start the logo journey off the gate wordmark
   *  onto the splash BEFORE clearing, so welcome/dashboard inherit ONE mark. */
  const doneAction = useCallback(() => {
    if (mode === 'gate') {
      startMfaExitLogoHandoff({ reduced: prefersReducedMotion() });
    }
    onRecoveryHoldChange?.(false);
    onEnrollmentComplete?.();
  }, [mode, onEnrollmentComplete, onRecoveryHoldChange]);

  const markLeftForApp = useCallback(() => setFromApp(true), []);

  return {
    step,
    setStep,
    payload,
    code,
    setCode,
    codeStatus,
    missCount,
    codes,
    codesFailed,
    busy,
    startError,
    fromApp,
    resumeFlow,
    resumeFactor: unverifiedFactorRef.current,
    startEnroll,
    goToCode,
    goBackToAdd,
    goBackToIntro,
    verify,
    startOver,
    cancelPending,
    doneAction,
    markLeftForApp,
  };
}
